import { CareerSiteFiltersOption } from 'api-clients/monolith';
import cx from 'classnames';
import { ActiveFiltersV2 } from 'contexts/v2/app-context';
import { ChevronDown } from 'lucide-react';
import React, { useId, useMemo, useState } from 'react';
import { IntlShape } from 'react-intl';

import { Separator } from 'components/ui/separator';

import { FilterConfig } from '../dropdown-section/filter-dropdown/types';
import { FilterCheckbox } from './filter-checkbox';

export const INITIAL_VISIBLE_COUNT = 5;
const LOAD_MORE_COUNT = 5;

interface FilterCheckboxSectionProps {
  filterKey: string;
  filter: FilterConfig;
  activeFiltersV2: ActiveFiltersV2;
  getActiveFilterKey: (key: string) => keyof ActiveFiltersV2;
  handleFilterChange: (
    type: string,
    option: { key: string; label: string },
    checked?: boolean,
  ) => void;
  isOptionChecked: (
    key: string,
    option: CareerSiteFiltersOption,
    activeFiltersV2: ActiveFiltersV2,
    getActiveFilterKey: (key: string) => keyof ActiveFiltersV2,
  ) => boolean;
  shouldExpandSection: (key: string) => boolean;
  setExpandedSections: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  intl: IntlShape;
  limitOptions?: boolean;
  // Controlled mode props (for mobile - state persists in parent)
  controlledVisibleCount?: number;
  onVisibleCountChange?: (count: number) => void;
}

export function CheckboxSection({
  filterKey,
  filter,
  activeFiltersV2,
  getActiveFilterKey,
  handleFilterChange,
  isOptionChecked,
  shouldExpandSection,
  setExpandedSections,
  intl,
  limitOptions = false,
  controlledVisibleCount,
  onVisibleCountChange,
}: FilterCheckboxSectionProps) {
  const panelId = useId();
  const isExpanded = shouldExpandSection(filterKey);
  const [localVisibleCount, setLocalVisibleCount] = useState(
    INITIAL_VISIBLE_COUNT,
  );

  const isControlled =
    controlledVisibleCount !== undefined && onVisibleCountChange !== undefined;

  const allOptions = useMemo(() => filter.options ?? [], [filter.options]);
  const totalCount = allOptions.length;

  const minVisibleForSelected = useMemo(() => {
    const activeFilterKey = getActiveFilterKey(filterKey);
    const selectedOptions = activeFiltersV2[activeFilterKey] ?? [];
    if (selectedOptions.length === 0) return INITIAL_VISIBLE_COUNT;

    let maxSelectedIndex = -1;
    allOptions.forEach((opt, idx) => {
      if (selectedOptions.some(s => s.key === opt.key)) {
        maxSelectedIndex = idx;
      }
    });

    return maxSelectedIndex >= 0 ? maxSelectedIndex + 1 : INITIAL_VISIBLE_COUNT;
  }, [activeFiltersV2, allOptions, filterKey, getActiveFilterKey]);

  const rawVisibleCount = isControlled
    ? controlledVisibleCount
    : localVisibleCount;
  const visibleCount = Math.max(rawVisibleCount, minVisibleForSelected);

  const shouldLimit = limitOptions && totalCount > INITIAL_VISIBLE_COUNT;
  const visibleOptions = shouldLimit
    ? allOptions.slice(0, visibleCount)
    : allOptions;
  const remainingCount = totalCount - visibleCount;
  const showMoreButton = shouldLimit && visibleCount < totalCount;

  const handleShowMore = () => {
    const newCount = Math.min(visibleCount + LOAD_MORE_COUNT, totalCount);
    if (isControlled) {
      onVisibleCountChange(newCount);
    } else {
      setLocalVisibleCount(newCount);
    }
  };

  return (
    <>
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? panelId : undefined}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-gray-50"
        onClick={() =>
          setExpandedSections(prev => ({
            ...prev,
            [filterKey]: !prev[filterKey],
          }))
        }
      >
        <span className="text-sm font-medium text-gray-900">
          {filter.label}
        </span>
        <ChevronDown
          className={cx(
            'h-5 w-5 text-gray-400 transition-transform duration-200',
            { 'rotate-180': isExpanded },
          )}
        />
      </button>
      {(filter.hasSeparator || (filterKey === 'city' && isExpanded)) && (
        <div className="px-4">
          <Separator />
        </div>
      )}
      {isExpanded && (
        <div id={panelId} className="bg-white p-4">
          <div
            className={cx('flex flex-col gap-1', {
              'max-h-38 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 overflow-y-auto':
                !shouldLimit,
            })}
          >
            {visibleOptions.length > 0 ? (
              visibleOptions.map(option => {
                const checkedVal = isOptionChecked(
                  filterKey,
                  option,
                  activeFiltersV2,
                  getActiveFilterKey,
                );
                return (
                  <FilterCheckbox
                    key={option.key}
                    label={option.label}
                    count={option.count ?? 0}
                    checked={checkedVal}
                    onChange={checked =>
                      handleFilterChange(filterKey, option, checked)
                    }
                  />
                );
              })
            ) : (
              <div className="py-2 text-sm text-gray-500">
                {intl.formatMessage({
                  defaultMessage: 'No options available',
                  description:
                    'Message shown when no filter options are available',
                })}
              </div>
            )}
          </div>
          {showMoreButton && (
            <button
              type="button"
              onClick={handleShowMore}
              className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              {intl.formatMessage(
                {
                  defaultMessage: 'Show more ({count})',
                  description: 'Button to show more filter options',
                },
                { count: remainingCount },
              )}
            </button>
          )}
        </div>
      )}
    </>
  );
}
